// src/pages/Customer/AddressesPage.tsx   (mevcut yolunuza göre güncelleyin)
import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  Home,
  FileText,
  Pencil,
  Trash2,
  Plus,
  CheckCircle2,
  X,
  Phone,
  UserRound,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { postRequest } from "../../../api/postRequest";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";

/* ==== Theme helpers (surface/divider) ==== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  // const isDark = luminance(hexToRgb(bg)) < 0.5;
  const isDark = luminance(hexToRgb(bg)) < 1;
  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)"  : "rgba(0,0,0,.06)",
    surface3: isDark ? "rgba(255,255,255,.085)" : "rgba(0,0,0,.085)",
    divider:  isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPri: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSec: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
  };
};
const panel = (lvl: 1 | 2, S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: lvl === 1 ? S.surface2 : S.surface1,
  borderColor: S.divider,
  borderWidth: 1,
  boxShadow: S.softShadow,
});

/* ==== Types (API ile aynı) ==== */
type ApiResponse<T> = { success: boolean; errorCode?: string; errorDetail?: string; data?: T; };

type AdresCardDto = {
  adresId: number;
  musteriId: number;
  baslik?: string;
  adSoyad?: string;
  telefon?: string;
  ulke?: string;
  sehir?: string;
  ilce?: string;
  postaKodu?: string;
  adresSatiri1?: string;
  adresSatiri2?: string;
  vergiDairesi?: string;
  vergiNo?: string;
  varsayilanFaturaMi: boolean;
  varsayilanTeslimatMi: boolean;
  silindiMi: boolean;
  olusturmaTarihi: string;
  guncellemeTarihi?: string | null;
};

type AdresListeResponse = { items: AdresCardDto[]; total: number; page: number; pageSize: number; };

type UpsertBase = {
  MusteriId: number;
  Baslik?: string;
  AdSoyad?: string;
  Telefon?: string;
  Ulke?: string;
  Sehir?: string;
  Ilce?: string;
  PostaKodu?: string;
  AdresSatiri1?: string;
  AdresSatiri2?: string;
  VergiDairesi?: string;
  VergiNo?: string;
  VarsayilanFaturaMi?: boolean;
  VarsayilanTeslimatMi?: boolean;
};

type AddReq = UpsertBase;
type UpdateReq = UpsertBase & { AdresId: number };

type VarsayilanReq = { MusteriId: number; AdresId: number; Tip: "fatura" | "teslimat" };
type SilReq = { MusteriId: number; AdresId: number };

/* ==== Helper UI ==== */
const Badge: React.FC<{ children: React.ReactNode; tone?: "primary" | "muted"; S: ReturnType<typeof useSurfaces> }> = ({ children, tone = "primary", S }) => {
  const style: React.CSSProperties =
    tone === "primary"
      ? { backgroundColor: "rgba(16,185,129,.12)", borderColor: "rgba(16,185,129,.25)", color: "#047857" }
      : { backgroundColor: S.surface2, borderColor: S.divider, color: S.textSec };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border" style={style}>
      {children}
    </span>
  );
};

const Line: React.FC<{ S: ReturnType<typeof useSurfaces> }> = ({ S }) => (
  <div className="h-px my-3" style={{ backgroundColor: S.divider }} />
);

const InputRow: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  required?: boolean;
  type?: string;
  S: ReturnType<typeof useSurfaces>;
}> = ({ label, value, onChange, placeholder, icon, required, type, S }) => (
  <label className="block">
    <div className="text-xs mb-1" style={{ color: S.textSec }}>
      {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
    </div>
    <div className="flex items-center gap-2 rounded-lg px-3 py-2 border" style={{ backgroundColor: S.surface2, borderColor: S.divider }}>
      {icon ? <span style={{ color: S.textSec }}>{icon}</span> : null}
      <input
        className="w-full outline-none bg-transparent text-sm"
        style={{ color: S.textPri }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type ?? "text"}
      />
    </div>
  </label>
);

/* ==== Address Form (bottom sheet / modal) ==== */
type FormProps = {
  open: boolean;
  onClose: () => void;
  initial?: Partial<AdresCardDto>;
  onSubmit: (payload: AddReq | UpdateReq) => Promise<void>;
  accent: string;
  musteriId: number;
  S: ReturnType<typeof useSurfaces>;
};

const AddressFormSheet: React.FC<FormProps> = ({ open, onClose, initial, onSubmit, accent, musteriId, S }) => {
  const isEdit = !!initial?.adresId;

  const [Baslik, setBaslik] = useState<string | undefined>(initial?.baslik ?? "");
  const [AdSoyad, setAdSoyad] = useState<string | undefined>(initial?.adSoyad ?? "");
  const [Telefon, setTelefon] = useState<string | undefined>(initial?.telefon ?? "");
  const [Ulke, setUlke] = useState<string | undefined>(initial?.ulke ?? "Türkiye");
  const [Sehir, setSehir] = useState<string | undefined>(initial?.sehir ?? "");
  const [Ilce, setIlce] = useState<string | undefined>(initial?.ilce ?? "");
  const [PostaKodu, setPostaKodu] = useState<string | undefined>(initial?.postaKodu ?? "");
  const [AdresSatiri1, setAdresSatiri1] = useState<string | undefined>(initial?.adresSatiri1 ?? "");
  const [AdresSatiri2, setAdresSatiri2] = useState<string | undefined>(initial?.adresSatiri2 ?? "");
  const [VergiDairesi, setVergiDairesi] = useState<string | undefined>(initial?.vergiDairesi ?? "");
  const [VergiNo, setVergiNo] = useState<string | undefined>(initial?.vergiNo ?? "");
  const [VarsayilanTeslimatMi, setVarsayilanTeslimatMi] = useState<boolean>(!!initial?.varsayilanTeslimatMi);
  const [VarsayilanFaturaMi, setVarsayilanFaturaMi] = useState<boolean>(!!initial?.varsayilanFaturaMi);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBaslik(initial?.baslik ?? "");
      setAdSoyad(initial?.adSoyad ?? "");
      setTelefon(initial?.telefon ?? "");
      setUlke(initial?.ulke ?? "Türkiye");
      setSehir(initial?.sehir ?? "");
      setIlce(initial?.ilce ?? "");
      setPostaKodu(initial?.postaKodu ?? "");
      setAdresSatiri1(initial?.adresSatiri1 ?? "");
      setAdresSatiri2(initial?.adresSatiri2 ?? "");
      setVergiDairesi(initial?.vergiDairesi ?? "");
      setVergiNo(initial?.vergiNo ?? "");
      setVarsayilanTeslimatMi(!!initial?.varsayilanTeslimatMi);
      setVarsayilanFaturaMi(!!initial?.varsayilanFaturaMi);
      setErr(null);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.adresId]);

  const canSubmit = useMemo(() => {
    return (AdSoyad?.trim()?.length ?? 0) > 0 && (AdresSatiri1?.trim()?.length ?? 0) > 0 && !submitting;
  }, [AdSoyad, AdresSatiri1, submitting]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setErr("Ad Soyad ve Adres Satırı zorunludur.");
      return;
    }
    setSubmitting(true);
    setErr(null);

    const payload: AddReq | UpdateReq = {
      MusteriId: musteriId,
      Baslik, AdSoyad, Telefon, Ulke, Sehir, Ilce, PostaKodu,
      AdresSatiri1, AdresSatiri2, VergiDairesi, VergiNo,
      VarsayilanFaturaMi, VarsayilanTeslimatMi,
      ...(isEdit ? { AdresId: initial!.adresId } : {}),
    };

    try {
      await onSubmit(payload);
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "İşlem başarısız.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120]">
      {/* overlay */}
      <div className="absolute inset-0" style={{  }} onClick={onClose} />
      {/* sheet */}
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="w-full md:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl border shadow-xl"
             style={{
              //  ...panel(2, S),
              backgroundColor: S.bg
              }}
             >
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: S.divider }}>
            <div className="font-semibold" style={{ color: S.textPri }}>{isEdit ? "Adresi Düzenle" : "Yeni Adres"}</div>
            <button className="p-2 rounded-md" style={{ color: S.textPri, backgroundColor: "transparent" }} onClick={onClose} aria-label="Kapat">
              <X size={18} />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <InputRow label="Adres Başlığı" value={Baslik} onChange={setBaslik} placeholder="Ev, İş, Aile…" icon={<Home size={16} />} S={S} />
            <InputRow label="Ad Soyad" value={AdSoyad} onChange={setAdSoyad} placeholder="Ad Soyad" icon={<UserRound size={16} />} required S={S} />
            <InputRow label="Telefon" value={Telefon} onChange={setTelefon} placeholder="05xx xxx xx xx" icon={<Phone size={16} />} S={S} />
            <div className="grid grid-cols-2 gap-3">
              <InputRow label="Şehir" value={Sehir} onChange={setSehir} placeholder="İstanbul" S={S} />
              <InputRow label="İlçe" value={Ilce} onChange={setIlce} placeholder="Kadıköy" S={S} />
              <InputRow label="Posta Kodu" value={PostaKodu} onChange={setPostaKodu} placeholder="34000" S={S} />
              <InputRow label="Ülke" value={Ulke} onChange={setUlke} placeholder="Türkiye" S={S} />
            </div>
            <InputRow label="Adres Satırı" value={AdresSatiri1} onChange={setAdresSatiri1} placeholder="Mah., Cad., Sk., No, Daire" icon={<MapPin size={16} />} required S={S} />
            <InputRow label="Adres Devamı" value={AdresSatiri2} onChange={setAdresSatiri2} placeholder="Apartman, Kat vb." S={S} />

            <Line S={S} />

            <div className="grid grid-cols-2 gap-3">
              <InputRow label="Vergi Dairesi (ops)" value={VergiDairesi} onChange={setVergiDairesi} placeholder="-" icon={<FileText size={16} />} S={S} />
              <InputRow label="Vergi No (ops)" value={VergiNo} onChange={setVergiNo} placeholder="-" icon={<FileText size={16} />} S={S} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 p-3 rounded-lg border" style={{ backgroundColor: S.surface2, borderColor: S.divider }}>
                <input
                  type="checkbox"
                  className="accent-emerald-600"
                  style={{ accentColor: S.accent }}
                  checked={VarsayilanTeslimatMi}
                  onChange={(e) => setVarsayilanTeslimatMi(e.target.checked)}
                />
                <span className="text-sm" style={{ color: S.textPri }}>Varsayılan Teslimat</span>
              </label>
              <label className="flex items-center gap-2 p-3 rounded-lg border" style={{ backgroundColor: S.surface2, borderColor: S.divider }}>
                <input
                  type="checkbox"
                  className="accent-emerald-600"
                  style={{ accentColor: S.accent }}
                  checked={VarsayilanFaturaMi}
                  onChange={(e) => setVarsayilanFaturaMi(e.target.checked)}
                />
                <span className="text-sm" style={{ color: S.textPri }}>Varsayılan Fatura</span>
              </label>
            </div>

            {err && <div className="text-sm" style={{ color: "#ef4444" }}>{err}</div>}

            <div className="pt-1" />
          </div>

          <div className="p-4 border-t" style={{ borderColor: S.divider }}>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full h-11 rounded-lg text-white font-medium disabled:opacity-60"
              style={{ background: accent }}
            >
              {isEdit ? "Kaydet" : "Adres Ekle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==== Main Page ==== */
const AddressesPage: React.FC = () => {
  const { theme } = useTheme();
  const { customer } = useCustomerAuth();
  const S = useSurfaces(theme);

  const accent = S.accent;
  const textPri = S.textPri;
  const textSec = S.textSec;

  const mid = customer?.id ?? 0;

  const [list, setList] = useState<AdresCardDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editItem, setEditItem] = useState<AdresCardDto | undefined>(undefined);

  const load = async () => {
    if (!mid) return;
    try {
      setLoading(true);
      setError(null);
      const payload = { MusteriId: mid, Page: 1, PageSize: 50, Sort: "yeni" as const };
      const res = await postRequest<typeof payload, ApiResponse<AdresListeResponse>>("/Adresler/listele", payload);
      if (!res.success) throw new Error(res.errorDetail || "Adresler yüklenemedi.");
      setList(res.data?.items ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid]);

  const addOrUpdate = async (payload: AddReq | UpdateReq) => {
    if (!payload.MusteriId) throw new Error("MusteriId gerekli");
    if ("AdresId" in payload) {
      const res = await postRequest<UpdateReq, ApiResponse<string>>("/Adresler/guncelle", payload);
      if (!res.success) throw new Error(res.errorDetail || "Güncelleme başarısız.");
    } else {
      const res = await postRequest<AddReq, ApiResponse<number>>("/Adresler/ekle", payload);
      if (!res.success) throw new Error(res.errorDetail || "Ekleme başarısız.");
    }
    await load();
  };

  const remove = async (item: AdresCardDto) => {
    if (!window.confirm("Bu adresi silmek istiyor musunuz?")) return;
    const payload: SilReq = { MusteriId: mid, AdresId: item.adresId };
    const res = await postRequest<SilReq, ApiResponse<string>>("/Adresler/sil", payload);
    if (!res.success) {
      alert(res.errorDetail || "Silme başarısız.");
    }
    await load();
  };

  const setDefault = async (item: AdresCardDto, tip: "teslimat" | "fatura") => {
    const payload: VarsayilanReq = { MusteriId: mid, AdresId: item.adresId, Tip: tip };
    const res = await postRequest<VarsayilanReq, ApiResponse<string>>("/Adresler/varsayilan-ayarla", payload);
    if (!res.success) {
      alert(res.errorDetail || "Güncelleme başarısız.");
    }
    await load();
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-base font-semibold" style={{ color: textPri }}>
          Adreslerim
        </h2>
        <p className="text-xs" style={{ color: textSec }}>
          Teslimat ve fatura adreslerinizi yönetin.
        </p>
      </div>
      <button
        onClick={() => {
          setEditItem(undefined);
          setSheetOpen(true);
        }}
        className="inline-flex items-center gap-2 rounded-lg h-10 px-3 text-white"
        style={{ background: accent }}
      >
        <Plus size={16} />
        <span>Yeni Adres</span>
      </button>
    </div>
  );

  return (
    <div className="space-y-4" style={{ backgroundColor: S.bg }}>
      {/* Başlık + Ekle */}
      {header}

      {/* Hata/Loading */}
      {loading && (
        <div className="rounded-xl border p-6" style={panel(1, S)}>
          <div className="animate-pulse">
            <div className="h-4 w-40 rounded mb-3" style={{ backgroundColor: S.surface2 }} />
            <div className="h-10 w-full rounded mb-2" style={{ backgroundColor: S.surface2 }} />
            <div className="h-10 w-full rounded" style={{ backgroundColor: S.surface2 }} />
          </div>
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl border p-4 text-sm" style={panel(1, S)}>
          <span style={{ color: "#ef4444" }}>{error}</span>
        </div>
      )}

      {/* Liste boş */}
      {!loading && list.length === 0 && (
        <div className="rounded-xl border p-6 text-center text-sm" style={panel(1, S)}>
          <span style={{ color: textSec }}>Henüz adres eklenmemiş. İlk adresinizi ekleyin.</span>
          <div className="mt-3">
            <button
              onClick={() => {
                setEditItem(undefined);
                setSheetOpen(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg h-10 px-3 text-white"
              style={{ background: accent }}
            >
              <Plus size={16} />
              <span>Adres Ekle</span>
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="grid grid-cols-1 gap-3">
        {list.map((it) => {
          const fullAddr = `${it.adresSatiri1 ?? ""}${it.adresSatiri2 ? " " + it.adresSatiri2 : ""}${
            it.ilce ? ", " + it.ilce : ""
          }${it.sehir ? ", " + it.sehir : ""}${it.postaKodu ? " " + it.postaKodu : ""}`;

          return (
            <div key={it.adresId} className="rounded-2xl border p-4" style={panel(2, S)}>
              {/* Üst satır */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ backgroundColor: S.surface2, color: S.textPri }}>
                    <MapPin size={18} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold" style={{ color: S.textPri }}>{it.baslik || "Adres"}</div>
                      {it.varsayilanTeslimatMi && (
                        <Badge S={S}>
                          <Home size={12} />
                          Teslimat
                        </Badge>
                      )}
                      {it.varsayilanFaturaMi && (
                        <Badge tone="muted" S={S}>
                          <FileText size={12} />
                          Fatura
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm mt-1" style={{ color: S.textPri }}>{fullAddr || "-"}</div>
                    <div className="text-xs mt-1" style={{ color: S.textSec }}>
                      {(it.adSoyad || "") + (it.telefon ? " • " + it.telefon : "")}
                    </div>
                  </div>
                </div>

                {/* Aksiyonlar (desktop) */}
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={() => { setEditItem(it); setSheetOpen(true); }}
                    className="h-9 px-3 rounded-md border text-sm transition"
                    style={panel(1, S)}
                  >
                    <div className="inline-flex items-center gap-1" style={{ color: S.textPri }}>
                      <Pencil size={14} />
                      Düzenle
                    </div>
                  </button>
                  <button
                    onClick={() => remove(it)}
                    className="h-9 px-3 rounded-md border text-sm transition"
                    style={{ ...panel(1, S), color: "#b91c1c", borderColor: "rgba(239,68,68,.35)" }}
                  >
                    <div className="inline-flex items-center gap-1">
                      <Trash2 size={14} />
                      Sil
                    </div>
                  </button>
                </div>
              </div>

              {/* Aksiyonlar (mobil) */}
              <div className="sm:hidden mt-3 flex items-center gap-2">
                <button
                  onClick={() => { setEditItem(it); setSheetOpen(true); }}
                  className="flex-1 h-10 rounded-md border text-sm"
                  style={panel(1, S)}
                >
                  Düzenle
                </button>
                <button
                  onClick={() => remove(it)}
                  className="flex-1 h-10 rounded-md border text-sm"
                  style={{ ...panel(1, S), color: "#b91c1c", borderColor: "rgba(239,68,68,.35)" }}
                >
                  Sil
                </button>
              </div>

              <Line S={S} />

              {/* Varsayılan ayarları */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={() => setDefault(it, "teslimat")}
                  className="h-10 rounded-md border text-sm inline-flex items-center justify-center gap-2 transition"
                  style={{
                    ...panel(1, S),
                    ...(it.varsayilanTeslimatMi
                      ? { backgroundColor: "rgba(16,185,129,.12)", borderColor: "rgba(16,185,129,.25)", color: "#047857" }
                      : {}),
                  }}
                >
                  <Home size={16} />
                  <span>Varsayılan Teslimat</span>
                  {it.varsayilanTeslimatMi && <CheckCircle2 size={16} />}
                </button>
                <button
                  onClick={() => setDefault(it, "fatura")}
                  className="h-10 rounded-md border text-sm inline-flex items-center justify-center gap-2 transition"
                  style={{
                    ...panel(1, S),
                    ...(it.varsayilanFaturaMi
                      ? { backgroundColor: "rgba(59,130,246,.12)", borderColor: "rgba(59,130,246,.25)", color: "#1d4ed8" }
                      : {}),
                  }}
                >
                  <FileText size={16} />
                  <span>Varsayılan Fatura</span>
                  {it.varsayilanFaturaMi && <CheckCircle2 size={16} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating add button (mobil) */}
      <button
        onClick={() => { setEditItem(undefined); setSheetOpen(true); }}
        className="sm:hidden fixed right-5 bottom-24 z-[60] w-12 h-12 rounded-full shadow-lg text-white grid place-items-center"
        style={{ background: accent }}
        aria-label="Adres ekle"
      >
        <Plus size={20} />
      </button>

      {/* Sheet / Modal */}
      <AddressFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        initial={editItem}
        onSubmit={addOrUpdate}
        accent={accent}
        musteriId={mid}
        S={S}
      />
    </div>
  );
};

export default AddressesPage;
